import i18next from 'i18next';
import { z } from 'zod';
import { TenantUserRoleSchema } from '@/entities/tenantusers/api/generated/model';

/**
 * パスワード複雑性ルール
 * - 8〜64 文字
 * - 英大文字・英小文字・数字・記号をそれぞれ 1 文字以上含む
 *
 * エラーメッセージは supportText で総合的に明示しているため、
 * 個別違反時もすべて同一の総合メッセージを返す（UX 一貫性のため）。
 */
const PASSWORD_COMPLEXITY_MESSAGE =
  'パスワードは英大文字・英小文字・数字・記号をそれぞれ1文字以上含む8文字以上で入力してください。';

const passwordSchema = z
  .string()
  .min(8, PASSWORD_COMPLEXITY_MESSAGE)
  .max(64, PASSWORD_COMPLEXITY_MESSAGE)
  .regex(/[A-Z]/, PASSWORD_COMPLEXITY_MESSAGE)
  .regex(/[a-z]/, PASSWORD_COMPLEXITY_MESSAGE)
  .regex(/\d/, PASSWORD_COMPLEXITY_MESSAGE)
  .regex(/[^A-Za-z0-9]/, PASSWORD_COMPLEXITY_MESSAGE);

/**
 * 年度ごとの所属組織エントリ
 */
const userDivisionEntrySchema = z.object({
  year: z.string().min(1),
  division_id: z.string(),
});

/**
 * 年度ごとの所属組織配列
 *
 * - 1 つ以上の所属組織が必要（API の minItems: 1 に対応）
 * - 同じ年度に複数の組織は登録できない
 * - 年度は連続している必要がある（例: 2024 と 2026 のみで 2025 が抜ける状態は不可）
 *
 * エラーメッセージは superRefine 経由で i18next.t() を使い、翻訳ファイルから取得する。
 */
const userDivisionsSchema = z.array(userDivisionEntrySchema).superRefine((entries, ctx) => {
  if (entries.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18next.t('user-management.users.divisions.validation.divisions-required'),
    });
    return;
  }

  // 各エントリの division_id 必須チェック
  entries.forEach((entry, index) => {
    if (!entry.division_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [index, 'division_id'],
        message: i18next.t('user-management.users.divisions.validation.division-required'),
      });
    }
  });

  // 年度重複チェック
  const years = entries.map((e) => e.year);
  if (new Set(years).size !== years.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18next.t('user-management.users.divisions.validation.duplicate-year'),
    });
  }

  // 連続性チェック（虫食い禁止）
  const sortedYears = [...years].map(Number).sort((a, b) => a - b);
  for (let i = 1; i < sortedYears.length; i++) {
    const current = sortedYears[i];
    const prev = sortedYears[i - 1];
    if (current === undefined || prev === undefined) continue;
    if (current !== prev + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('user-management.users.divisions.validation.non-continuous-years'),
      });
      break;
    }
  }
});

/**
 * ユーザー作成フォームのバリデーションスキーマ
 *
 * 制約値は TenantUserCreateRequest の OpenAPI 定義に準拠
 */
export const userCreateSchema = z.object({
  email: z.string().email().max(255),
  name: z.string().min(1).max(255),
  divisions: userDivisionsSchema,
  role: z.nativeEnum(TenantUserRoleSchema),
  password: passwordSchema,
});

/**
 * ユーザー編集フォームのバリデーションスキーマ
 *
 * email と password は別経路で扱う（email は変更不可、password はリセットフォーム）
 */
export const userEditSchema = z.object({
  name: z.string().min(1).max(255),
  divisions: userDivisionsSchema,
  role: z.nativeEnum(TenantUserRoleSchema),
  is_active: z.boolean(),
});

/**
 * パスワードリセットフォームのバリデーションスキーマ
 */
export const resetPasswordSchema = z.object({
  password: passwordSchema,
});

export type UserDivisionEntry = z.infer<typeof userDivisionEntrySchema>;
export type UserCreateFormValues = z.infer<typeof userCreateSchema>;
export type UserEditFormValues = z.infer<typeof userEditSchema>;
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;
